// DS: C25C
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import { useOnOutsideClick } from '@wix/da-hooks/pkg/useOnOutsideClick';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import UserAvatar from '../User/Avatar/UserAvatar';
import PositionOnlyPopperPopover from '@wix/da-ds/pkg/popper/presets/PositionOnlyPopperPopover';
import StandardMenu from '@wix/da-ds/pkg/Dropdown/MenuPreset/StandardMenu';
import CloseBig from '@wix/da-ds/pkg/Icons/CloseBig';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import s from './UserSearchField.scss';
const noop = ()=>{};
const UserSearchField = ({ placeholder, onUserSelect, onSearch, onNoMatchingUsers, candidates, onBlur = noop, excludedUsers, className, inputClassName, withClearButton, popupFixedPosition: popupPositionFixed, inputRef: inputRefFromProps, onClear = noop, onInputKeyDown, usePortal = true, isChatContext, disabled })=>{
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const [highlightedMenuItemIndex, setHighlightedMenuItemIndex] = useState(0);
    const inputRefDefault = useRef(null);
    const rootRef = useRef(null);
    const menuRef = useRef(null);
    const [matchingUsers, setMatchingUsers] = useState([]);
    const inputRef = inputRefFromProps || inputRefDefault;
    const { current: dropdownId } = useRef(uniqueId('dropdown'));
    // focus input on mount
    useEffect(()=>{
        inputRef.current && inputRef.current.focus();
    }, [
        inputRef
    ]);
    const onClearClick = useCallback(()=>{
        setInputValue('');
        onClear();
    }, [
        onClear
    ]);
    const onOutsideClick = useCallback(()=>{
        setInputValue('');
        setMatchingUsers([]);
        onBlur();
    }, [
        onBlur
    ]);
    const onSelect = useCallback((user)=>{
        setInputValue('');
        setMatchingUsers([]);
        onUserSelect(user);
        inputRef.current && inputRef.current.focus();
    }, [
        onUserSelect,
        inputRef
    ]);
    useOnOutsideClick([
        rootRef,
        menuRef,
        inputRef
    ], onOutsideClick);
    const removeExcludedUsers = useCallback((found)=>{
        const newMatchingUsers = excludedUsers ? found.filter((u)=>!excludedUsers.some((u2)=>u2.userId === u.userId || u2.useridUuid === u.useridUuid)) : found;
        if (newMatchingUsers.length === 0) {
            onNoMatchingUsers === null || onNoMatchingUsers === void 0 ? void 0 : onNoMatchingUsers(inputValue);
        }
        setMatchingUsers(newMatchingUsers);
    }, [
        excludedUsers,
        onNoMatchingUsers,
        inputValue
    ]);
    // set search query
    useEffect(()=>{
        // we need a minimum of 3 search characters
        if (inputValue.length >= 3) {
            if (candidates) {
                const foundUsers = candidates.filter(({ username })=>username.toLowerCase().startsWith(inputValue.toLowerCase()));
                removeExcludedUsers(foundUsers);
            } else {
                onSearch(inputValue, removeExcludedUsers, {
                    isChatContext
                });
            }
        } else {
            setMatchingUsers([]);
        }
    }, [
        onSearch,
        inputValue,
        candidates,
        removeExcludedUsers,
        isChatContext
    ]);
    // arrow keys for menu navigation
    const onRootKeyDown = useCallback((e)=>{
        if (matchingUsers.length === 0) {
            return;
        }
        switch(e.key){
            case 'ArrowUp':
                setHighlightedMenuItemIndex(highlightedMenuItemIndex === 0 ? matchingUsers.length - 1 : highlightedMenuItemIndex - 1);
                break;
            case 'ArrowDown':
                setHighlightedMenuItemIndex(highlightedMenuItemIndex === matchingUsers.length - 1 ? 0 : highlightedMenuItemIndex + 1);
                break;
            case 'Enter':
                if (highlightedMenuItemIndex < matchingUsers.length) {
                    onSelect(matchingUsers[highlightedMenuItemIndex]);
                }
                break;
            default:
                break;
        }
    }, [
        highlightedMenuItemIndex,
        matchingUsers,
        onSelect
    ]);
    const menuItems = useMemo(()=>matchingUsers.map((user, index)=>({
                value: user.userId,
                highlighted: index === highlightedMenuItemIndex,
                onMouseEnter: ()=>setHighlightedMenuItemIndex(index),
                onClick: ()=>{
                    onSelect(user);
                },
                label: /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(UserAvatar, {
                    user: user,
                    size: 26,
                    className: s['autocomplete-avatar'],
                    withLink: false
                }), /*#__PURE__*/ React.createElement("span", null, user.username))
            })), [
        highlightedMenuItemIndex,
        matchingUsers,
        onSelect
    ]);
    return /*#__PURE__*/ React.createElement(PositionOnlyPopperPopover, {
        className: classnames(s['root'], withClearButton && s['with-clear-button'], className),
        ref: rootRef,
        onKeyDown: onRootKeyDown,
        popoverContent: /*#__PURE__*/ React.createElement(StandardMenu, {
            id: dropdownId,
            ref: menuRef,
            itemClassName: s['autocomplete-result'],
            items: menuItems
        }),
        placement: "bottom",
        secondaryPlacement: "start",
        isVisible: matchingUsers.length > 0,
        usePortal: usePortal,
        positionFixed: popupPositionFixed
    }, /*#__PURE__*/ React.createElement("input", {
        ref: inputRef,
        type: "text",
        className: classnames(s['username-input'], inputClassName),
        value: inputValue,
        onChange: (e)=>setInputValue(e.target.value),
        onKeyDown: onInputKeyDown,
        placeholder: placeholder,
        disabled: disabled
    }), withClearButton && /*#__PURE__*/ React.createElement(PlainButtonOrLink, {
        className: s['clear'],
        onClick: onClearClick
    }, /*#__PURE__*/ React.createElement(VisuallyHidden, null, t('userSearchField.clear')), /*#__PURE__*/ React.createElement(CloseBig, {
        size: IconSize.TINY,
        className: s['clear-icon']
    })));
};
UserSearchField.displayName = 'UserSearchField';
export default UserSearchField;
